import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

export type KioskOrderStatusPreviewMetafield = {
  id: string;
  handle: string;
  value: string;
};

export type KioskOrderStatusPreviewShop = {
  id: string;
  name: string;
  kiosk_additional_information?: string | null;
};

export type KioskOrderStatusPreviewChildOrder = {
  id: string;
  shop: KioskOrderStatusPreviewShop | null;
};

export type KioskOrderStatusPreviewOrder = {
  id: string;
  number: string;
  status: 'PAYMENT' | 'READY';
  ready_at: string | null;
  child_orders?: readonly KioskOrderStatusPreviewChildOrder[];
  metafields: readonly KioskOrderStatusPreviewMetafield[] | null;
  shop: KioskOrderStatusPreviewShop | null;
};

export type KioskOrderStatusBlockScenarioContent = {
  order: KioskOrderStatusPreviewOrder | undefined;
  orderReadyAt: string;
  receiptPrinted: boolean;
};
export const KIOSKORDERSTATUSBLOCK_LEGACY_FIELD_ORDER = [
  "header_image",
  { type: 'separator' },
  "background_image",
  "background_color",
  "logo",
  "heading",
  { type: 'separator' },
  "footer",
  { type: 'groupedFields', handle: 'footer_styles', fields: ["button_background_color","button_text_color","displayOrderReadyAt"] }
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  kioskOrderStatusBlockDescriptor: _generatedKioskOrderStatusBlockDescriptor,
  kioskOrderStatusBlockBlocDefinition,
  kioskOrderStatusBlockDashboardLayout,
  kioskOrderStatusBlockConfig,
  kioskOrderStatusBlockResolveConfig,
  kioskOrderStatusBlockProperties,
  kioskOrderStatusBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Kiosk order status",
    componentType: 'KioskOrderStatusBlockComponent',
    category: 'content',
    description: "Kiosk order status.",
    keywords: [],
    allowed_in: ["OrderStatusPage"],
    allowedIn: [{ bloc: 'OrderStatusPageComponent', slot: 'body' }],
    sharedFields: {}
  },
  () => ({
    header_image: propertyValue({
      type: 'image',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-order-status.header-image.label',
        help: 'pages.blocks.kiosk-order-status.header-image.help',
      }
    }),
    background_image: propertyValue({
      type: 'image',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-order-status.background-image.label',
        help: 'pages.blocks.kiosk-order-status.background-image.help',
      }
    }),
    background_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-50"},
      options: {
        label: 'pages.blocks.kiosk-order-status.background-color.label',
      }
    }),
    text_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-800"},
      options: {
        label: 'pages.blocks.kiosk-order-status.text-color.label',
      }
    }),
    logo: propertyValue({
      type: 'image',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-order-status.logo.label',
        help: 'pages.blocks.kiosk-order-status.logo.help',
      }
    }),
    heading: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-order-status.heading.label',
        help: 'pages.blocks.kiosk-order-status.heading.help',
      }
    }),
    fill_viewport: propertyValue({
      type: 'checkbox',
      value: true,
      options: {
        label: 'pages.blocks.kiosk-order-status.fill-viewport.label',
        help: 'pages.blocks.kiosk-order-status.fill-viewport.help',
      },
    }),
    footer: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: 'pages.blocks.kiosk-order-status.footer.label',
        help: 'pages.blocks.kiosk-order-status.footer.help',
      }
    }),
    footer_styles: {
    background_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"neutral-50"},
      options: {
        label: 'pages.blocks.kiosk-order-status.footer-styles-background-color.label',
      }
    }),
    button_background_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"primary-800"},
      options: {
        label: 'pages.blocks.kiosk-order-status.button-background-color.label',
      }
    }),
    button_text_color: propertyValue({
      type: 'color',
      value: {"type":"hex","value":"#FFFFFF"},
      options: {
        label: 'pages.blocks.kiosk-order-status.button-text-color.label',
      }
    }),
    displayOrderReadyAt: propertyValue({
      type: 'checkbox',
      value: true,
      options: {
        label: 'pages.blocks.kiosk-order-status.display-order-ready-at.label',
      }
    })
    }
  }),
  () => ({
    omitFields: ['text_color', 'footer_styles.background_color'] as const,
    tabs: [
      {
        label: 'pages.blocks.kiosk-order-status.tab.content.label',
        fieldsets: [
          {
            legend: 'pages.blocks.kiosk-order-status.fieldset.header-image.label',
            fields: ['header_image'],
          },
          {
            legend: 'pages.blocks.kiosk-order-status.fieldset.background.label',
            fields: ['background_image', 'background_color'],
          },
          {
            legend: 'pages.blocks.kiosk-order-status.fieldset.title.label',
            fields: ['heading'],
          },
          {
            legend: 'pages.blocks.kiosk-order-status.fieldset.pickup-time.label',
            fields: ['footer_styles.displayOrderReadyAt'],
          },
          {
            legend: 'pages.blocks.kiosk-order-status.fieldset.footer-text.label',
            fields: ['footer'],
          },
          {
            legend: 'pages.blocks.kiosk-order-status.fieldset.logo.label',
            fields: ['logo'],
          },
          {
            legend: 'pages.blocks.kiosk-order-status.fieldset.button.label',
            fields: [
              'footer_styles.button_background_color',
              'footer_styles.button_text_color',
            ],
          },
          {
            legend: 'pages.blocks.kiosk-order-status.fieldset.layout.label',
            fields: ['fill_viewport'],
          },
        ],
      },
    ],
  })
);

export function kioskOrderStatusBlockDescriptor(): IBlockDescriptor;
export function kioskOrderStatusBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function kioskOrderStatusBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedKioskOrderStatusBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(_generatedKioskOrderStatusBlockDescriptor, kioskOrderStatusBlockProperties, fieldOrder);
}

export {
  kioskOrderStatusBlockBlocDefinition,
  kioskOrderStatusBlockDashboardLayout,
  kioskOrderStatusBlockProperties,
  kioskOrderStatusBlockConfig,
  kioskOrderStatusBlockResolveConfig,
  kioskOrderStatusBlockDefinitionSetupScenarios,
};

export type KioskOrderStatusBlockConfig = typeof kioskOrderStatusBlockConfig;
